import { useAllVoices } from '@/hooks/useAllVoices';
import CloneVoice from '@/images/CloneVoice.svg';
import FaceFemale from '@/images/FaceFemale.svg';
import FaceMale from '@/images/FaceMale.svg';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { IVoice } from '@d-id/types';
import styled from '@emotion/styled';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { AgentVoiceModal } from './AgentVoiceModal';

const VoiceButtonContainer = styled.div<{ isLoading: boolean }>`
    padding: 8px 8px 8px 12px;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.black[300]};
    cursor: ${({ isLoading }) => (isLoading ? 'default' : 'pointer')};

    &:hover {
        ${({ isLoading }) => !isLoading && `border: 1px solid ${theme.colors.secondary.main};`}
    }
`;

const VoiceName = styled.span<{ hasValue: boolean }>`
    font-size: 12px;
    color: ${({ hasValue }) => (hasValue ? theme.colors.secondary.main : theme.colors.black[700])};
`;

const ModalButton = styled(ButtonBase)`
    display: flex;
    padding: 2px 8px;
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
    background-color: ${theme.colors.black[100]};
    border-radius: 8px;
    margin-left: auto;

    :hover {
        background-color: ${theme.colors.black[200]};
    }
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;

    gap: 8px;
    width: 100%;
`;

const genderImage: Record<string, string> = {
    female: FaceFemale,
    male: FaceMale,
    'non-binary': CloneVoice,
};

const VoiceIcon = ({ value }: { value: IVoice | null }) => {
    const gender = value?.gender;

    if (!gender || gender === 'unspecified') {
        return <img loading="lazy" width="20" height="20" src={CloneVoice} />;
    }

    return <img loading="lazy" width="20" height="20" src={genderImage[gender.toLowerCase()]} />;
};

export const VoiceButton = observer(() => {
    const { isLoading } = useAllVoices();
    const agentEditorStore = useAgentEditorStore();
    const [voiceModalState, setVoiceModalState] = useState(false);

    const voice = agentEditorStore.ttsSelectedVoice;

    return (
        <>
            <VoiceButtonContainer isLoading={isLoading} onClick={isLoading ? () => {} : () => setVoiceModalState(true)}>
                {isLoading ? (
                    <LoaderWrapper>
                        <CircularProgress size={20} />
                    </LoaderWrapper>
                ) : (
                    <>
                        <VoiceIcon value={voice} />
                        <VoiceName hasValue={!!voice?.name}>{voice?.name || 'Add a voice'}</VoiceName>
                        <ModalButton>{!!voice?.name ? 'Change' : 'Add voice'}</ModalButton>
                    </>
                )}
            </VoiceButtonContainer>
            <AgentVoiceModal open={voiceModalState} onClose={() => setVoiceModalState(false)} />
        </>
    );
});
